import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { BRAND } from '../brand';
import type { Profile } from '../lib/api';

// Importadas y no referenciadas por ruta: así Vite les pone un hash de
// contenido en el nombre. El servidor manda los estáticos con `immutable` y un
// año de caché, de modo que un nombre fijo dejaría a quien ya vio una versión
// anterior atrapado en ella durante ese año.
import podioWebp from '../assets/portada-podio.webp';
import podioWebp2x from '../assets/portada-podio@2x.webp';
import podioPng from '../assets/portada-podio.png';

interface Props {
  profile: Profile | null;
  onJoin: () => void;
  onCreate: () => void;
}

/**
 * Portada.
 *
 * Dos columnas en escritorio: mensaje a la izquierda, ilustración a la
 * derecha. En móvil la ilustración no se muestra y el texto se compacta,
 * para que las pestañas del catálogo asomen sin tener que bajar.
 */

/**
 * `true` cuando la ventana es de escritorio.
 *
 * Se usa para NO renderizar la ilustración en móvil. Ocultarla con CSS no
 * basta: el navegador descarga igualmente los 100 KB de una imagen que nadie
 * va a ver, y lo hace justo en las conexiones que peor lo llevan.
 */
function useEscritorio(): boolean {
  const consulta = '(min-width: 1024px)';
  const [esEscritorio, setEsEscritorio] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(consulta).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(consulta);
    const alCambiar = () => setEsEscritorio(mq.matches);
    mq.addEventListener('change', alCambiar);
    alCambiar();
    return () => mq.removeEventListener('change', alCambiar);
  }, []);

  return esEscritorio;
}

export const Hero: React.FC<Props> = ({ profile, onJoin, onCreate }) => {
  const esEscritorio = useEscritorio();

  return (
  <section className="max-w-7xl mx-auto px-4 pt-4 pb-5 sm:pt-8 sm:pb-8 lg:pt-10 lg:pb-10">
    <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-3 sm:gap-5 lg:gap-12 items-center">
      {/* ---------------- Mensaje ---------------- */}
      <div className="text-center lg:text-left">
        <h1 className="text-2xl sm:text-4xl xl:text-5xl font-display font-black leading-[1.08] text-balance">
          Trivias en vivo que <span className="text-brand-gradient">no cuestan nada</span>
        </h1>

        <p className="mt-2 sm:mt-3 text-ink-300 text-sm sm:text-base leading-relaxed max-w-xl mx-auto lg:mx-0 text-balance">
          {BRAND.openClaim} Crea un cuestionario, abre una sala con PIN y juega con hasta{' '}
          {BRAND.maxPlayersPerRoom} personas a la vez.
        </p>

        <div className="mt-4 sm:mt-6 grid grid-cols-2 sm:flex sm:flex-row gap-2 sm:gap-3 justify-center lg:justify-start">
          <button
            onClick={onJoin}
            className="px-3 sm:px-7 py-3 sm:py-3.5 rounded-xl font-bold text-sm sm:text-base bg-ember-500 hover:bg-ember-400
                       text-on-accent transition-colors shadow-lg shadow-ember-500/20"
          >
            Entrar con un PIN
          </button>
          <button
            onClick={onCreate}
            className="flex items-center justify-center gap-2 px-3 sm:px-7 py-3 sm:py-3.5 rounded-xl font-bold text-sm sm:text-base
                       bg-ink-850 hover:bg-ink-800 border border-ink-700 text-ink-100 transition-colors"
          >
            <Plus className="w-4.5 h-4.5" />
            Crear un quiz
          </button>
        </div>

        {!profile && (
          <p className="mt-2 sm:mt-3 text-ink-500 text-xs sm:text-[0.8125rem]">
            Puedes jugar sin cuenta. La necesitas solo para crear quizzes y guardar tu progreso.
          </p>
        )}
      </div>

      {/* ---------------- Ilustración ----------------

          Sin marco, sin borde y sin esquinas redondeadas a propósito: el fondo
          está recortado (ver scripts/preparar-portada.cjs), así que la
          ilustración se apoya directamente sobre la página y se funde con ella
          en los dos temas. Cualquier contenedor visible la delataría como una
          estampa pegada encima. */}
      {esEscritorio && (
        <picture>
          <source
            type="image/webp"
            srcSet={`${podioWebp} 760w, ${podioWebp2x} 1200w`}
            sizes="34vw"
          />
          <img
            src={podioPng}
            // `width`/`height` reservan el hueco antes de que la imagen llegue:
            // sin ellos el texto de al lado da un salto al cargar.
            width={1906}
            height={1765}
            alt="Tres jóvenes en un podio celebrando: la primera sostiene un trofeo y los otros dos llevan medallas."
            loading="eager"
            fetchPriority="high"
            className="w-auto max-h-[330px] mx-auto select-none"
            draggable={false}
          />
        </picture>
      )}
    </div>
  </section>
  );
};
